import { Box, Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { useGetUserNode } from '/@/hooks/nodes';
import { useCurrentCampaignYear } from '/@/hooks/zones';
import { _ } from '/@/locale';
import { getNodeScopeFilters, type NodeScopeFilters } from '/@/utils/node-scope';
import DashboardSubheader from '../../../components/dashboard-sub-header';
import CropSurfaceReportRankingDashboardCard from '../../components/crop-surface-report-ranking-dashboard-card';
import FieldsRankingReportDashboardCard from '../../components/fields-ranking-report-dashboard-card';
import GrowersList from '../../components/growers-list';
import GrowersRankingReportDashboardCard from '../../components/growers-ranking-report-dashboard-card';
import HybridRankingReportDashboardCard from '../../components/hybrids-ranking-report-dashboard-card';

export const PastReportsDashboardCards = ({
    year,
    growerNodeId,
    isRv = true,
    nodeScope,
}: {
    growerNodeId: number;
    year: number;
    isRv: boolean;
    nodeScope: NodeScopeFilters;
}) => {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1fr 1fr', xs: '1fr' }, gap: 2 }}>
            <CropSurfaceReportRankingDashboardCard growerNodeId={growerNodeId} year={year} nodeScope={nodeScope} />
            {isRv ? (
                <GrowersRankingReportDashboardCard
                    growerNodeId={growerNodeId}
                    year={year}
                    crop='corn'
                    nodeScope={nodeScope}
                />
            ) : (
                <FieldsRankingReportDashboardCard
                    growerNodeId={growerNodeId}
                    year={year}
                    crop='corn'
                    nodeScope={nodeScope}
                />
            )}
            <HybridRankingReportDashboardCard
                growerNodeId={growerNodeId}
                year={year}
                crop='corn'
                nodeScope={nodeScope}
            />
        </Box>
    );
};

export const PastReportsDashboard = ({ rvUserNodeQuery }: { rvUserNodeQuery: ReturnType<typeof useGetUserNode> }) => {
    const currentCampaignYear = useCurrentCampaignYear();
    const previousCampaignYear = useMemo(() => {
        return currentCampaignYear !== null ? currentCampaignYear - 1 : null;
    }, [currentCampaignYear]);
    const form = useForm<{
        year: number;
    }>({
        defaultValues: {
            year: previousCampaignYear ?? new Date().getFullYear() - 1,
        },
    });

    // Update form value when previousCampaignYear becomes available
    useEffect(() => {
        if (previousCampaignYear !== null) {
            form.setValue('year', previousCampaignYear);
        }
    }, [previousCampaignYear, form]);

    return (
        <>
            <Stack direction='column' spacing={3}>
                <DashboardSubheader
                    title={_('Reportes de campaña')}
                    subtitle={_(
                        'Accedé al resumen de resultados de los lotes cada productor para analizar el rendimiento y planificar la próxima campaña.'
                    )}
                    form={form}
                />
                {rvUserNodeQuery.data?.id && (
                    <PastReportsDashboardCards
                        growerNodeId={rvUserNodeQuery.data?.id}
                        year={form.watch('year')}
                        isRv={true}
                        nodeScope={getNodeScopeFilters('growers')}
                    />
                )}
            </Stack>
            {rvUserNodeQuery.data && (
                <GrowersList
                    userNodeId={rvUserNodeQuery.data?.id}
                    tab='past-reports'
                    year={form.watch('year')}
                    orderBy='reports'
                    order='desc'
                />
            )}
        </>
    );
};
